/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **An exit is not a decision, and a nav away is an exit** — `#1245`, closing the half of `#1129`
 * that was written into the rule and built nowhere.
 *
 * `CLAUDE.md` · *An exit is not a decision — prompt before discarding work* names four exits: Escape,
 * a ✕, a click on the backdrop, **and a nav away**. The first three were built for the wizard in
 * `#1129`; the fourth reached no surface at all, so navigating away from any editing surface in this
 * console discarded unsaved work silently. *A rule attached to one path lapses when another is added*,
 * one more time — and here the rule was written wider than its own fix on the day it was ratified.
 *
 * ## A registry of claims, not a dirty flag
 *
 * Several surfaces can hold unsaved work at once — an in-place section on a detail page, a wizard
 * over it — so the question *would leaving lose anything* is **any claim true**, not one component's
 * boolean. Each surface registers a **function**, evaluated at the moment of the exit:
 *
 * - A boolean would have to be recomputed and pushed up on every keystroke, which puts a re-render of
 *   the shell behind every character typed into an editor.
 * - A function is asked **once**, when somebody tries to leave, which is the only moment the answer
 *   is needed and the only moment it is certainly current.
 *
 * ## One guard, several callers — and a `<Link>` is one of them
 *
 * `#1129`'s first property is *one path*: three call sites will not stay in step, which is how the
 * wizard's Escape came to bypass the ✕'s guard. That is a rule about **one implementation**, not one
 * call site, and it has to be, because a `<Link>` cannot call a function — it navigates by being
 * clicked. So a guarded link stops its own default and routes through {@link useGuardedNavigate},
 * which is the same implementation the shell's buttons use.
 *
 * ## What this does **not** cover, declared rather than left to be discovered
 *
 * An in-page `navigate()` — a page moving the operator itself, rather than the shell doing it — is
 * not guarded. There are 31 navigation call sites in this console and guarding each is the failure
 * this rule exists to prevent, so the honest answer is **one blocker at the router**, which needs
 * `useBlocker`, which needs a data router. `App.tsx` builds its routes as JSX inside `<BrowserRouter>`
 * with the auth-loading effect in the same component, so that is a shell change rather than a
 * one-liner: **`#1294`**, filed with this rather than after it.
 *
 * Scoped this way by Todd, 2026-09-10, on the measurement that both reported paths — a shell nav menu
 * click, and the wizard's Next — are reachable without it.
 *
 * ## The provider throws when it is missing, and that is deliberate
 *
 * A permissive default would make an unwrapped surface report *nothing to lose*, which is a **false
 * claim** rather than a missing feature — the distinction `X33a` draws — and it is the one answer a
 * guard must never give by accident. A test that forgets the provider fails loudly instead of
 * asserting against a guard that is silently switched off.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from './ConfirmDialog';
import { withoutRecordedVersions } from '../shared/editors/shared-expression-version';

/**
 * **Has this draft diverged from what is stored** — the claim most surfaces want, said once.
 *
 * Key order is normalised before comparing, which is not fussiness: a draft is built from the stored
 * value and then edited immutably, so spreads reorder keys without changing a single value. Comparing
 * raw `JSON.stringify` would report *dirty* for an edit nobody made, and a prompt that fires when
 * nothing changed is how an operator learns to click through the prompt that matters.
 */
export function hasDiverged(draft: unknown, stored: unknown): boolean {
  const stable = (value: unknown): string =>
    JSON.stringify(value, (key, v: unknown) =>
      // A recorded version is attention, not an edit: opening a row writes one (`#1302`).
      withoutRecordedVersions(key, v) === undefined
        ? undefined
        : v && typeof v === 'object' && !Array.isArray(v)
          ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
          : v,
    );
  return stable(draft) !== stable(stored);
}

/** What a surface holding unsaved work answers when somebody tries to leave. */
export type UnsavedWorkClaim = () => boolean;

interface UnsavedWorkGuardApi {
  /** Register a claim; returns the unregister. */
  register: (claim: UnsavedWorkClaim) => () => void;
  /** Is anything unsaved right now — the `beforeunload` question, and the dialog's gate. */
  hasUnsavedWork: () => boolean;
  /**
   * Ask, and resolve `true` when the exit may proceed.
   *
   * Resolves `true` immediately when nothing is at stake, so a caller never has to ask whether to
   * ask — *one path* means the guarded navigate is the only navigate, not the slow one.
   */
  confirmExit: () => Promise<boolean>;
  /**
   * **Re-ask the claims and tell anyone showing the answer** (`LP13`). Called by a claiming surface
   * after each of its renders, so a title bar reading {@link useHasUnsavedWork} follows a draft as it
   * diverges and as it is saved, without any surface computing and pushing a boolean of its own.
   */
  notify: () => void;
  /** Subscribe to {@link notify}; returns the unsubscribe. */
  subscribe: (listener: () => void) => () => void;
}

const UnsavedWorkGuardContext = React.createContext<UnsavedWorkGuardApi | null>(null);

function useGuardApi(hook: string): UnsavedWorkGuardApi {
  const api = React.useContext(UnsavedWorkGuardContext);
  if (!api) {
    throw new Error(
      `${hook} requires <UnsavedWorkGuardProvider>. See UnsavedWorkGuard.tsx — a guard that ` +
        'defaults to "nothing to lose" is a false claim, so this is loud rather than permissive.',
    );
  }
  return api;
}

/**
 * **Declare that this surface is holding unsaved work.**
 *
 * The claim is read through a ref, so a surface re-registers only when the provider changes rather
 * than on every keystroke — the closure stays current without the effect re-running.
 */
export function useUnsavedWork(claim: UnsavedWorkClaim): void {
  const { register, notify } = useGuardApi('useUnsavedWork');
  const latest = React.useRef(claim);
  // Kept current in a layout effect rather than during render (`react-hooks/refs`): it runs before any
  // exit can be attempted and before the notify below asks.
  React.useLayoutEffect(() => {
    latest.current = claim;
  });
  React.useEffect(() => register(() => latest.current()), [register]);
  // After every render of the claiming surface, because that is when its answer can have changed.
  // A reader re-renders only when the answer flips, so a keystroke costs a re-ask, never a re-render
  // of the shell (the reason the registry holds functions, above).
  React.useEffect(() => notify());
}

/**
 * **Is anything unsaved right now, as a value a component can render** (`LP13`, `#1122`).
 *
 * The title bar states whether leaving would lose work. It reads the **same** registry the exit guard
 * asks, so the bar and the prompt cannot disagree: `#1122` measured two meanings of *dirty* on one
 * page, and a third one here would be the defect it was filed for.
 */
export function useHasUnsavedWork(): boolean {
  const { subscribe, hasUnsavedWork } = useGuardApi('useHasUnsavedWork');
  return React.useSyncExternalStore(subscribe, hasUnsavedWork, () => false);
}

/**
 * **Ask, without navigating.** For an exit that is not a route change — signing out, which throws the
 * session away and takes any unsaved work with it, and cannot be guarded after the fact because the
 * auth clear happens first.
 */
export function useConfirmExit(): () => Promise<boolean> {
  return useGuardApi('useConfirmExit').confirmExit;
}

/**
 * **Navigate, having asked.** The shell's one way to move the operator.
 *
 * Same signature shape as `useNavigate`'s path form, so adopting it at a call site is a rename.
 */
export function useGuardedNavigate(): (to: string) => void {
  const navigate = useNavigate();
  const { confirmExit } = useGuardApi('useGuardedNavigate');
  return React.useCallback(
    (to: string) => {
      void confirmExit().then((mayLeave) => {
        if (mayLeave) navigate(to);
      });
    },
    [confirmExit, navigate],
  );
}

/**
 * A `<Link>`'s `onClick`, for the nav that is rendered as an anchor rather than a button.
 *
 * It stops the browser's own navigation and re-issues it through the same guard, so the drawer's
 * links and the sidebar's links cannot answer differently from the top bar's buttons.
 */
export function useGuardedLinkProps(to: string, onNavigate?: () => void): { onClick: (e: React.MouseEvent) => void } {
  const go = useGuardedNavigate();
  return {
    onClick: (e: React.MouseEvent) => {
      // Let the browser have the gestures that mean *somewhere else entirely* — a new tab is not an
      // exit from this surface, so guarding it would prompt about work the operator is not leaving.
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      onNavigate?.();
      go(to);
    },
  };
}

export const UnsavedWorkGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const claims = React.useRef(new Set<UnsavedWorkClaim>());
  const [pending, setPending] = React.useState<{ resolve: (ok: boolean) => void } | null>(null);

  const listeners = React.useRef(new Set<() => void>());
  const notify = React.useCallback(() => {
    for (const listener of listeners.current) listener();
  }, []);
  const subscribe = React.useCallback((listener: () => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const register = React.useCallback(
    (claim: UnsavedWorkClaim) => {
      claims.current.add(claim);
      return () => {
        claims.current.delete(claim);
        // A surface unmounting with a claim that was true takes the unsaved state with it.
        notify();
      };
    },
    [notify],
  );

  const hasUnsavedWork = React.useCallback(() => {
    for (const claim of claims.current) if (claim()) return true;
    return false;
  }, []);

  const confirmExit = React.useCallback(
    () => (hasUnsavedWork() ? new Promise<boolean>((resolve) => setPending({ resolve })) : Promise.resolve(true)),
    [hasUnsavedWork],
  );

  /**
   * **The browser's own exit gets the same registry and none of our UI.**
   *
   * Closing the tab or reloading loses exactly what a nav away loses, and it is the one exit we
   * cannot render a dialog for — the browser owns that prompt, deliberately, and setting
   * `returnValue` is the whole of the API.
   */
  React.useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedWork()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedWork]);

  const api = React.useMemo(
    () => ({ register, hasUnsavedWork, confirmExit, notify, subscribe }),
    [register, hasUnsavedWork, confirmExit, notify, subscribe],
  );

  return (
    <UnsavedWorkGuardContext.Provider value={api}>
      {children}
      {pending && (
        /* `ConfirmDialog` rather than a fourth hand-rolled confirm (`#1129`), and it names the
           outcome rather than the mechanism (`#1045`): what is lost is the editing, not "the page". */
        <ConfirmDialog
          title="Leave without saving?"
          body="You have changes that have not been saved. Leaving this page discards them."
          confirmLabel="Discard changes"
          cancelLabel="Stay here"
          tone="danger"
          onConfirm={() => {
            pending.resolve(true);
            setPending(null);
          }}
          onCancel={() => {
            pending.resolve(false);
            setPending(null);
          }}
        />
      )}
    </UnsavedWorkGuardContext.Provider>
  );
};
